import { useEffect, useMemo, useRef, useState } from 'react';
import { getSubChapters, IChapter, IPosition } from '../pages/Assistance.tsx';
import { ProgressRing } from './ProgressRing.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { useTranslation } from 'react-i18next';
import {
    faArrowLeftFromLine,
    faArrowRightFromLine,
    faChevronDown,
    faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { withIcon } from '../../../core/components/Icon.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';

const ChevronRightIcon = withIcon(faChevronRight);
const ChevronDownIcon = withIcon(faChevronDown);
const CollapseIcon = withIcon(faArrowLeftFromLine);
const ExpandIcon = withIcon(faArrowRightFromLine);

type NavItemType = 'chapter' | 'subchapter' | 'position';

interface NavItem {
    navKey: string;
    displayNumber: string;
    header: string;
    numberTotal: number;
    numberRelevant: number;
    numberCompleted: number;
    children: NavItem[];
    type: NavItemType;
    chapterKey?: string;
    positionIndex?: number;
}
const normaliseNum = (n: string) => n.replace(/\.$/, '');

const isPositionCompleted = (p: IPosition) =>
    p.isRelevant?.value === 'True' && (!p.articleSets?.length || p.articleSets.every((s) => s.isComplete));

const positionToNavItem = (pos: IPosition, chapterKey: string, navPrefix: string): NavItem => ({
    navKey: `${navPrefix}__pos__${pos.positionIndex}`,
    displayNumber: normaliseNum(pos.number?.value ?? ''),
    header: pos.text?.value?.split('\n')[0] || pos.header || '',
    numberTotal: 1,
    numberRelevant: pos.isRelevant?.value === 'True' ? 1 : 0,
    numberCompleted: isPositionCompleted(pos) ? 1 : 0,
    children: [],
    type: 'position',
    chapterKey,
    positionIndex: pos.positionIndex,
});

const buildNavTree = (chapterMap: Map<string, IChapter>): NavItem[] => {
    const nodeMap = new Map<string, NavItem>();

    chapterMap.forEach((chapter, originalKey) => {
        const normKey = normaliseNum(originalKey);

        let numberTotal = chapter.numberTotal || 0;
        let numberRelevant = chapter.numberRelevant || 0;
        let numberCompleted = chapter.numberCompleted || 0;

        // When positions are loaded, derive stats from them for real-time updates after mutations
        if (chapter.positions?.length > 0) {
            numberTotal = 0;
            numberRelevant = 0;
            numberCompleted = 0;
            for (const p of chapter.positions) {
                if (p.prediction === 'PreText') continue;
                numberTotal++;
                if (p.isRelevant?.value === 'True') {
                    numberRelevant++;
                    if (isPositionCompleted(p)) numberCompleted++;
                }
            }
        }

        nodeMap.set(normKey, {
            navKey: originalKey,
            displayNumber: normKey,
            header: chapter.header || '',
            numberTotal,
            numberRelevant,
            numberCompleted,
            children: [],
            type: 'chapter',
        });
    });

    const roots: NavItem[] = [];
    nodeMap.forEach((node, key) => {
        const parentKey = key.split('.').slice(0, -1).join('.');
        if (!parentKey || !nodeMap.has(parentKey)) {
            roots.push(node);
        } else {
            nodeMap.get(parentKey)!.children.push(node);
        }
    });

    nodeMap.forEach((node) => {
        if (node.children.length > 0) return;

        const chapter = chapterMap.get(node.navKey);
        if (!chapter?.positions?.length) return;

        const groups = getSubChapters(chapter.positions);
        const hasPreTextGroups = groups.some((g, i) => i > 0 && g[0]?.prediction === 'PreText');

        if (!hasPreTextGroups) {
            for (const p of chapter.positions) {
                if (p.prediction !== 'PreText') {
                    node.children.push(positionToNavItem(p, node.navKey, node.navKey));
                }
            }
            return;
        }

        groups.forEach((group, idx) => {
            const first = group[0];

            if (idx === 0) {
                for (const p of group) {
                    if (p.prediction !== 'PreText') {
                        node.children.push(positionToNavItem(p, node.navKey, node.navKey));
                    }
                }
                return;
            }

            if (first?.prediction !== 'PreText') return;

            let numberRelevant = 0;
            let numberCompleted = 0;
            const regularPositions: IPosition[] = [];

            for (const p of group) {
                if (p.prediction === 'PreText') continue;
                regularPositions.push(p);
                if (p.isRelevant?.value === 'True') {
                    numberRelevant++;
                    if (isPositionCompleted(p)) numberCompleted++;
                }
            }

            if (!regularPositions.length && !first.header && !first.text?.value) return;

            const displayNum = normaliseNum(first.number?.value ?? '');
            const navKey = displayNum ? `${node.navKey}__${displayNum}` : `${node.navKey}__sub__${idx}`;

            node.children.push({
                navKey,
                displayNumber: displayNum,
                header: first.header || '',
                numberTotal: regularPositions.length,
                numberRelevant,
                numberCompleted,
                children: regularPositions.map((p) => positionToNavItem(p, node.navKey, navKey)),
                type: 'subchapter',
                chapterKey: node.navKey,
                positionIndex: first.positionIndex,
            });
        });
    });

    return roots;
};

const findNavKeyPath = (targetKey: string, nodes: NavItem[]): string[] | null => {
    for (const node of nodes) {
        if (node.navKey === targetKey) return [node.navKey];
        const childPath = findNavKeyPath(targetKey, node.children);
        if (childPath) return [node.navKey, ...childPath];
    }
    return null;
};

const accordionExpandedSet = (targetKey: string, tree: NavItem[]): Set<string> => {
    if (tree.length > 0) {
        const path = findNavKeyPath(targetKey, tree);
        if (path) return new Set(path);
    }
    const parts = normaliseNum(targetKey).split('.').filter(Boolean);
    const keys = new Set<string>();
    for (let i = 1; i < parts.length; i++) keys.add(parts.slice(0, i).join('.'));
    keys.add(targetKey);
    return keys;
};
interface NavItemRowProps {
    item: NavItem;
    depth: number;
    currentChapter: string;
    expandedNodes: Set<string>;
    selectedPositionIndex?: number;
    firstChapterKey?: string;
    onToggleNode: (key: string) => void;
    onChapterClick: (chapterKey: string) => void;
    onAutoExpand?: (chapterKey: string) => void;
    onPositionClick?: (positionIndex: number, chapterKey: string) => void;
}

const NavItemRow = ({
    item,
    depth,
    currentChapter,
    expandedNodes,
    selectedPositionIndex,
    firstChapterKey,
    onToggleNode,
    onChapterClick,
    onAutoExpand,
    onPositionClick,
}: NavItemRowProps) => {
    const { t } = useTranslation('assistance');
    const isExpanded = expandedNodes.has(item.navKey);
    const visibleChildren = item.children.filter((c) => c.type !== 'position');
    const hasChildren = visibleChildren.length > 0;

    useEffect(() => {
        if (hasChildren && item.navKey === firstChapterKey && onAutoExpand) {
            onAutoExpand(firstChapterKey);
        }
    }, [hasChildren]);
    const pendingCount = item.numberRelevant - item.numberCompleted;
    const relevantText = t('listOfServicesView.navigation.tooltipRelevant', {
        relevant: item.numberRelevant,
    });
    const totalText = t('listOfServicesView.navigation.tooltipTotal');
    const incompleteText = t('listOfServicesView.navigation.tooltipIncomplete');

    const hasSelectedSubchapter =
        item.type === 'chapter' && item.children.some((c) => c.type === 'subchapter' && expandedNodes.has(c.navKey));

    const isSelected =
        (item.type === 'chapter' && currentChapter === item.navKey && !hasSelectedSubchapter) ||
        (item.type === 'subchapter' && expandedNodes.has(item.navKey) && currentChapter === item.chapterKey);

    const handleClick = () => {
        if (item.type === 'position') {
            onPositionClick?.(item.positionIndex!, item.chapterKey!);
        } else if (item.type === 'subchapter') {
            if (item.positionIndex !== undefined && onPositionClick) {
                onPositionClick(item.positionIndex, item.chapterKey!);
            } else {
                onChapterClick(item.chapterKey!);
            }
            onToggleNode(item.navKey);
        } else {
            onChapterClick(item.navKey);
            if (hasChildren) onToggleNode(item.navKey);
        }
    };

    const num = item.displayNumber;
    const label =
        item.type === 'chapter'
            ? `${num}${!num.includes('.') ? '.' : ''} ${item.header}`.trim()
            : `${num}${num && item.header ? ' ' : ''}${item.header}`;

    return (
        <div>
            <button
                className={classnames(
                    'flex items-center cursor-pointer gap-1.5 px-3 py-2 my-2 text-left text-xs rounded-md transition-colors',
                    item.type === 'subchapter' ? 'ml-10 mr-4 w-[calc(100%-3.5rem)]' : 'mx-4 w-[calc(100%-2rem)]',
                    isSelected
                        ? 'bg-surface-brand-medium-default text-content-brand-strong hover:bg-surface-brand-medium-hover'
                        : 'text-content-strong hover:bg-surface-strong'
                )}
                onClick={handleClick}
            >
                {item.type !== 'subchapter' && (
                    <span
                        className="flex-none w-4 flex items-center justify-center"
                        onClick={(e) => {
                            if (!hasChildren) return;
                            e.stopPropagation();
                            onToggleNode(item.navKey);
                        }}
                    >
                        {hasChildren &&
                            (isExpanded ? (
                                <ChevronDownIcon className="text-sm text-content-strong font-medium" />
                            ) : (
                                <ChevronRightIcon className="text-sm text-content-strong font-medium" />
                            ))}
                    </span>
                )}
                <span
                    className={classnames(
                        'flex-1 text-sm truncate min-w-0 text-content-default font-medium',
                        !hasChildren && 'font-normal',
                        isSelected ? 'text-content-brand-strong' : ''
                    )}
                >
                    {label}
                </span>
                {item.type !== 'position' &&
                    (item.type === 'subchapter' || !isExpanded || !hasChildren) &&
                    pendingCount > 0 && (
                        <Tooltip
                            content={
                                <div className="flex flex-col text-center">
                                    <span>
                                        <strong>{pendingCount}</strong>{' '}
                                        {pendingCount === 1
                                            ? t('listOfServicesView.navigation.tooltipIncompleteSingular')
                                            : incompleteText}
                                    </span>
                                </div>
                            }
                        >
                            <span className="flex-none w-5 h-5 flex items-center justify-center text-xs font-medium bg-surface-accent-strong-default text-content-white rounded-full">
                                {pendingCount}
                            </span>
                        </Tooltip>
                    )}
                {item.type !== 'position' && item.numberTotal > 0 && (
                    <Tooltip
                        className="flex-shrink min-w-0"
                        content={
                            <div className="flex flex-col text-center">
                                {pendingCount > 0 && (
                                    <span>
                                        <strong>{item.numberRelevant}</strong> {relevantText}
                                    </span>
                                )}
                                <span>
                                    <strong>{item.numberTotal}</strong> {totalText}
                                </span>
                            </div>
                        }
                    >
                        <span
                            className={classnames(
                                'flex-none w-[1rem] text-end text-content-strong tabular-nums',
                                isSelected && 'text-content-brand-strong'
                            )}
                        >
                            {item.numberTotal}
                        </span>
                    </Tooltip>
                )}
            </button>

            {isExpanded &&
                visibleChildren.map((child) => (
                    <NavItemRow
                        key={child.navKey}
                        item={child}
                        depth={depth + 1}
                        currentChapter={currentChapter}
                        expandedNodes={expandedNodes}
                        selectedPositionIndex={selectedPositionIndex}
                        onToggleNode={onToggleNode}
                        onChapterClick={onChapterClick}
                        onPositionClick={onPositionClick}
                    />
                ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface ChapterNavigationProps {
    chapterMap: Map<string, IChapter> | null;
    currentChapter: string;
    selectedPositionIndex?: number;
    totals: { numberTotal: number; numberRelevant: number; numberRelevantCompleted: number };
    onChapterClick: (chapter: string) => void;
    onAutoExpand?: (chapterKey: string) => void;
    onPositionClick?: (positionIndex: number, chapterKey: string) => void;
    onOverviewClick?: () => void;
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
}

export const ChapterNavigation = ({
    chapterMap,
    currentChapter,
    selectedPositionIndex,
    totals,
    onChapterClick,
    onAutoExpand,
    onPositionClick,
    onOverviewClick,
    isCollapsed,
    onToggleCollapsed,
}: ChapterNavigationProps) => {
    const { t } = useTranslation('assistance');

    const navTree = useMemo(() => (chapterMap ? buildNavTree(chapterMap) : []), [chapterMap]);

    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() =>
        currentChapter ? accordionExpandedSet(currentChapter, []) : new Set()
    );

    const lastExpandedChapterRef = useRef('');
    useEffect(() => {
        if (!navTree.length) return;
        if (!currentChapter) {
            setExpandedNodes(new Set());
            lastExpandedChapterRef.current = '';
            return;
        }
        if (currentChapter === lastExpandedChapterRef.current) return;
        lastExpandedChapterRef.current = currentChapter;

        setExpandedNodes(accordionExpandedSet(currentChapter, navTree));
    }, [currentChapter, navTree]);

    const handleToggleNode = (key: string) => {
        setExpandedNodes((prev) => {
            if (prev.has(key)) {
                const next = new Set(prev);
                next.delete(key);
                return next;
            }
            return accordionExpandedSet(key, navTree);
        });
    };

    const handleOverviewClick = () => {
        setExpandedNodes(new Set());
        onAutoExpand(navTree[0]?.navKey);
        onOverviewClick?.();
    };

    const progressPercent =
        totals.numberRelevant > 0 ? (totals.numberRelevantCompleted / totals.numberRelevant) * 100 : 0;

    if (isCollapsed) {
        return (
            <div className="flex flex-col items-center pt-3 h-full bg-surface-white">
                <button
                    className="w-8 h-8 cursor-pointer flex items-center justify-center rounded hover:bg-surface-weak transition-colors text-content-medium"
                    onClick={onToggleCollapsed}
                    title={t('listOfServicesView.navigation.expand')}
                >
                    <ExpandIcon className="text-sm text-content-default text-[16px]" />
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-surface-white border-r border-line-weak overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between px-3 py-3 flex-none">
                <div className="flex flex-col gap-2 flex-1 min-w-0 bg-surface-white p-3 rounded-sm">
                    <div className="flex items-center justify-between gap-3 pb-3">
                        <a
                            onClick={handleOverviewClick}
                            className="text-sm font-semibold text-content-strong truncate cursor-pointer"
                        >
                            {t('listOfServicesView.navigation.projectOverview')}
                        </a>
                        <button
                            className="flex-none w-8 h-8 flex items-center justify-center rounded cursor-pointer hover:bg-surface-weak transition-colors text-content-medium"
                            onClick={onToggleCollapsed}
                            title={t('listOfServicesView.navigation.collapse')}
                        >
                            <CollapseIcon className="text-sm text-content-default text-[16px]" />
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <ProgressRing progress={progressPercent} size={28} strokeWidth={4} />
                        <div className="flex flex-row justify-between w-full">
                            <span className="text-xs text-content-strong leading-tight">
                                {totals.numberRelevant === 0 ? (
                                    t('listOfServicesView.noRelevantPositions')
                                ) : (
                                    <>
                                        <span className="font-semibold text-content-strong">
                                            {totals.numberRelevantCompleted} {t('listOfServicesView.of')}
                                            {totals.numberRelevant}
                                        </span>{' '}
                                        <span className="font-regular">
                                            {t('listOfServicesView.relevantPositionsCompleted')}
                                        </span>
                                        <span className="text-content-default font-regular">{` (${((totals.numberRelevantCompleted / totals.numberRelevant) * 100).toFixed(2)}%) `}</span>
                                    </>
                                )}
                            </span>
                            <span className="text-xs text-content-strong leading-tight">
                                <span className="font-semibold">{totals.numberTotal}</span>{' '}
                                {t('listOfServicesView.totalPositions')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chapter tree */}
            <div className="flex-1 overflow-y-auto py-1 overflow-x-hidden">
                {navTree.map((item) => (
                    <NavItemRow
                        key={item.navKey}
                        item={item}
                        depth={0}
                        currentChapter={currentChapter}
                        firstChapterKey={navTree[0]?.navKey}
                        expandedNodes={expandedNodes}
                        selectedPositionIndex={selectedPositionIndex}
                        onToggleNode={handleToggleNode}
                        onChapterClick={onChapterClick}
                        onAutoExpand={onAutoExpand}
                        onPositionClick={onPositionClick}
                    />
                ))}
            </div>
        </div>
    );
};
